import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@cbrebuild/blocks';
import countries from '../../data/countries.json';
import states from '../../data/states.json';
import Select from '../../nucleus/select/select';
import Tooltip from '../../nucleus/tooltip/tooltip';
import { isUnitedStatesCountry, isValidAddress1, isValidCity, isValidPostal } from './voucher-validation';
import './address-form.scss';

const AddressForm = ({
  postal = '',
  city = '',
  address = '',
  address2 = '',
  territory = '',
  country = '',
  isRelocating = false,
  onChange,
  onBlur,
  showValidation,
  isRequired,
  showAddressLine2,
  addressTooltip,
  preAgreementId,
  preAgreementIdInputId = 'pre-agreement-id',
}) => {
  // validation logic
  const postalIsValid = useMemo(() => isValidPostal(postal, country), [postal, country]);
  const addressIsValid = useMemo(() => isValidAddress1(address), [address]);
  const cityIsValid = useMemo(() => isValidCity(city), [city]);
  const [countryName, setCountryName] = useState('');
  const shouldShowAddressLine2 = showAddressLine2 === undefined ? !isRelocating : showAddressLine2;
  const addressLabel = `Address${shouldShowAddressLine2 ? ' Line 1' : ''}`;
  const showRequiredValidation = isRequired && showValidation;
  const postalIsRequired = isRequired && isUnitedStatesCountry(country);
  // Non-digit ZIP (e.g. "adsda") -- shown immediately, unlike the "is required" message
  // above which only appears once the page-level `showValidation` flag is set. `isValidPostal`
  // only enforces this format for US addresses, so this is always false for other countries.
  const postalFormatInvalid = !!postal && !postalIsValid;

  useEffect(() => {
    if (isUnitedStatesCountry(country)) {
      setCountryName('United States');
    } else {
      setCountryName(country);
    }
  }, [country]);

  // updates labels and territories on country change
  const {
    filteredStates, postalLabel, stateLabel,
  } = useMemo(() => {
    const isUSA = isUnitedStatesCountry(country);
    const found = countries.find(c => c.display_name === countryName);
    return {
      filteredStates: states.filter(s => s.country_id === found?.country_id),
      stateLabel: isUSA ? 'State' : 'Province / Region',
      postalLabel: isUSA ? 'Zip' : 'Postal Code',
    };
  }, [country, countryName]);

  const handleCountryChange = (e) => {
    const target = { name: 'country', type: 'select-one', value: e.display_name };
    setCountryName(target.value);
    onChange({ target });
  };
  const handleStateChange = (e) => {
    const target = { name: 'territory', type: 'select-one', value: e.display_name };
    onChange({ target });
  };
  const countryField = (
    <div className={preAgreementId !== undefined ? 'field select-subType col-2' : 'field select-subType'}>
      <label>
        {isRequired && <span className={(countryName === '') ? 'asterisk-error' : ''}>*</span>} Country
      </label>
      <Select
        className={isRequired && countryName === '' ? 'errorInput' : ''}
        defaultOption={countryName || 'Select'}
        options={countries}
        onChange={e => handleCountryChange(e)}
        // Country/state are fetched asynchronously (e.g. re-fetched after navigating back
        // to this step) -- without `onClear`, Select locks onto whatever `defaultOption`
        // it first mounted with and never updates once the real saved value arrives.
        onClear
      />
      {(isRequired && countryName === '') && <p className="asterisk-error top10">Country is required</p>}
    </div>
  );

  return (
    <div className="address-form">
      {preAgreementId !== undefined ? (
        <div className="form-row form-row--pre-agreement">
          {countryField}
          <div className="field-group pre-agreement-field">
            <label htmlFor={preAgreementIdInputId}>Pre-Agreement ID</label>
            <input
              id={preAgreementIdInputId}
              type="text"
              name="pre_agreement_id"
              maxLength={50}
              value={preAgreementId || ''}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
        </div>
      ) : countryField}
      <div className="form-row">
        <div className="field-group col-2">
          <label>
            {isRequired && <span className={`${!addressIsValid && showRequiredValidation ? 'asterisk-error' : ''}`}>* </span>}
            {addressLabel}
            {addressTooltip && (
              <Tooltip
                message={(
                  <>
                    <h3>{addressTooltip.title}</h3>
                    <br />
                    <p>{addressTooltip.text}</p>
                  </>
                )}
              >
                <Icon iconName="info-circle" />
              </Tooltip>
            )}
          </label>
          <input
            type="text"
            name="address"
            className={`${!addressIsValid && showRequiredValidation ? 'errorInput' : ''}`}
            value={address || ''}
            onChange={onChange}
            onBlur={onBlur}
          />
          {!addressIsValid && showRequiredValidation ? <p className="asterisk-error">{addressLabel} is required</p> : ''}
        </div>
        {shouldShowAddressLine2 && (
          <div className="field-group col-2">
            <label>Address Line 2</label>
            <input
              type="text"
              name="address2"
              value={address2 || ''}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
        )}
      </div>
      <div className="form-row">
        <div className="field-group col-2">
          <label>{isRequired && <span className={`${!cityIsValid && showRequiredValidation ? 'asterisk-error' : ''}`}>* </span>} City</label>
          <input
            type="text"
            name="city"
            className={`${!cityIsValid && showRequiredValidation ? 'errorInput' : ''}`}
            value={city || ''}
            onChange={onChange}
            onBlur={onBlur}
          />
          {!cityIsValid && showRequiredValidation ? <p className="asterisk-error">City is required</p> : ''}
        </div>
        <div className="field col-4">
          <label>
            {isRequired && <span className={(territory === '') ? 'asterisk-error' : ''}>*</span>} {stateLabel}
          </label>
          <Select
            className={isRequired && territory === '' ? 'selectErrorInput' : ''}
            defaultOption={territory || 'Select'}
            options={filteredStates}
            onChange={e => handleStateChange(e)}
            onClear
          />
          {(isRequired && territory === '') && <p className="asterisk-error">State is required</p>}
        </div>
        <div className="field col-4">
          <label>{postalIsRequired && <span className={`${!postalIsValid && showRequiredValidation ? 'asterisk-error' : ''}`}>* </span>} {postalLabel}</label>
          <input
            type="text"
            name="postal"
            className={`${(!postalIsValid && showRequiredValidation) || postalFormatInvalid ? 'errorInput' : ''}`}
            value={postal || ''}
            onChange={onChange}
            onBlur={onBlur}
          />
          {postalFormatInvalid && <p className="asterisk-error">{postalLabel} must be 5 digits or ZIP+4</p>}
          {!postalFormatInvalid && !postalIsValid && showRequiredValidation ? <p className="asterisk-error">{postalLabel} is required</p> : ''}
        </div>
      </div>
    </div>
  );
};

AddressForm.propTypes = {
  postal: PropTypes.string,
  city: PropTypes.string,
  address: PropTypes.string,
  address2: PropTypes.string,
  territory: PropTypes.string,
  country: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  isRelocating: PropTypes.bool,
  showValidation: PropTypes.bool,
  isRequired: PropTypes.bool,
  showAddressLine2: PropTypes.bool,
  preAgreementId: PropTypes.string,
  preAgreementIdInputId: PropTypes.string,
  addressTooltip: PropTypes.shape({
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }),
};

AddressForm.defaultProps = {
  postal: '',
  city: '',
  address: '',
  address2: '',
  territory: '',
  country: '',
  isRelocating: false,
  onBlur: () => { },
  showValidation: false,
  isRequired: true,
  showAddressLine2: undefined,
  addressTooltip: null,
};

export default AddressForm;
